import { IconButton } from '@fox/atoms/src/icon-button/index';
import { Select } from '@fox/atoms/src/select/index';
import { Spin } from '@fox/atoms/src/spin/index';
import { Typography } from '@fox/atoms/src/typography/index';
import { FlexColumn, FlexRow } from '@fox/layout/src/flex';
import { PaddedY } from '@fox/layout/src/style';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useResponsiveTableContext } from './context';

const MobileViewHeaderWrapper = styled.div`
	display: grid;
	z-index: 1;
	grid-template-columns: 1fr 40px;
	background: ${props => props.theme.colors.paleGrey};
	align-items: center;
	border: 1px solid ${props => props.theme.colors.charcoal};
	height: 3rem;
	border-radius: 5px;
`;

const MobileViewNoDataWrapper = styled.div`
	z-index: 1;
	display: flex;
	justify-content: center;
	align-items: center;
	border: 1px solid ${props => props.theme.colors.charcoal};
	height: 3rem;
	border-radius: 5px;
`;

const MobileHeaderItemWrapper = styled.div`
	justify-content: center;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	@media (max-width: 450px) {
		.columnTitle {
			display: none;
		}
	}
`;

const MobileViewWrapper = styled.div`
	flex-direction: column;
	display: flex;
	width: 100%;
	gap: 0.5rem;
`;

const MobileOpenViewWrapper = styled.div`
	display: flex;
	flex-direction: column;
	padding: 0rem 0.5rem;
	justify-content: flex-start;
`;

const MobileItemWrapper = styled.div`
	text-align: left;
`;

const MobileHeaderTitleWrapper = styled.div`
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
	max-width: 300px;
	@media (max-width: 400px) {
		max-width: 200px;
	}
`;

const MobileViewHeaderOpenWrapper = styled.div<{ $isOpen: boolean }>`
	display: flex;
	flex-direction: column;
	gap: 0.2rem;
	padding-top: 0.5rem;
	opacity: ${props => (props.$isOpen ? 1 : 0)};
	max-height: ${props => (props.$isOpen ? '5000px' : 0)};
	transition: ${props =>
		props.$isOpen
			? 'max-height 0.3s ease-in-out,  opacity 0.5s ease-in-out'
			: 'max-height 0.5s ease-in-out,  opacity 0.1s ease-in-out'};
`;

function MobileHeaderItem({ dataIndex, record }: { dataIndex: string; record: unknown }) {
	const { columns } = useResponsiveTableContext();
	const column = columns.find(c => c.dataIndex === dataIndex);
	if (!column || !record) return <></>;
	return (
		<MobileHeaderItemWrapper>
			<Typography className="columnTitle" style={{ fontWeight: 'bold' }}>
				{column.title}:
			</Typography>
			{column.mobileHeaderRender ? (
				column.mobileHeaderRender(record)
			) : column.mobileRender ? (
				column.mobileRender(record)
			) : column.render ? (
				column.render(record)
			) : (
				<MobileHeaderTitleWrapper>{record[dataIndex]}</MobileHeaderTitleWrapper>
			)}
		</MobileHeaderItemWrapper>
	);
}

function MobileViewHeader({ record, dataIndexesInHeader }: { record: unknown; dataIndexesInHeader: string[] }) {
	const { columns, mobileRowActionComponent, rowsExpandedOnSmallScreens } = useResponsiveTableContext();
	const [isOpen, setIsOpen] = useState<boolean>(rowsExpandedOnSmallScreens ?? false);

	if (!record) return <></>;

	const Component = mobileRowActionComponent?.(record);

	return (
		<>
			<MobileViewHeaderWrapper onClick={() => setIsOpen(prev => !prev)}>
				<FlexRow style={{ justifyContent: 'space-around' }}>
					{dataIndexesInHeader.map((dataIndex, i) => {
						return <MobileHeaderItem key={`mob_${dataIndex}_${i}`} dataIndex={dataIndex} record={record} />;
					})}
				</FlexRow>
				<IconButton iconName={isOpen ? 'ArrowDropUp' : 'ArrowDropDown'} />
			</MobileViewHeaderWrapper>

			<MobileViewHeaderOpenWrapper $isOpen={isOpen}>
				{columns
					.filter(c => !c.isActionColumn && !c.hideOnMobile)
					.map(column => {
						return (
							<React.Fragment key={column.dataIndex}>
								<MobileOpenViewWrapper key={column.dataIndex}>
									<MobileItemWrapper style={{ fontWeight: 'bold' }}>{column.title}</MobileItemWrapper>
									<MobileItemWrapper>
										{column.mobileRender
											? column.mobileRender(record)
											: column.render
											? column.render(record)
											: column.dataIndex
											? record[column.dataIndex]
											: ''}
									</MobileItemWrapper>
								</MobileOpenViewWrapper>
							</React.Fragment>
						);
					})}
				{Component ? (
					<PaddedY>
						<MobileOpenViewWrapper>{Component}</MobileOpenViewWrapper>
					</PaddedY>
				) : null}
			</MobileViewHeaderOpenWrapper>
		</>
	);
}

export function MobileView() {
	const { data, columns, sortProps, loading } = useResponsiveTableContext();

	const sortColumns = columns
		?.filter(c => c.sortable)
		.reduce<{ options: { id: string; dataIndex: string; title: string; asc: boolean }[]; counter: number }>(
			(prev, cur) => {
				if (!cur.dataIndex) return prev;
				prev.options.push({
					id: `${prev.counter}`,
					dataIndex: cur.dataIndex,
					title: `${cur.title} (asc)`,
					asc: true
				});
				prev.counter++;
				prev.options.push({
					id: `${prev.counter}`,
					dataIndex: cur.dataIndex,
					title: `${cur.title} (desc)`,
					asc: false
				});
				return prev;
			},
			{ options: [], counter: 0 }
		);

	return (
		<Spin spinning={loading}>
			<MobileViewWrapper>
				{sortProps ? (
					<FlexRow style={{ justifyContent: 'center', padding: '1rem' }}>
						<Select
							options={sortColumns.options}
							sx={{ width: '200px' }}
							onChange={(_, v) => {
								if (!v) return;
								const col = sortColumns.options.find(c => c.id === v.id);
								if (!col) return;
								sortProps.onSort({ column: col.dataIndex, asc: col.asc });
							}}
							size="medium"
							placeholder="Sort by column"
							disableClearable
							value={sortColumns.options.find(c => c.id === sortProps?.sort?.column)}
							getOptionLabel={(o: { id: string; dataIndex: string; title: string; asc: boolean }) => o.title}
							isOptionEqualToValue={(o, v) => v.id === o.id}
						/>
					</FlexRow>
				) : null}
				{(data?.length ?? 0) < 1 ? (
					<MobileViewNoDataWrapper>
						<MobileHeaderTitleWrapper>No data to display</MobileHeaderTitleWrapper>
					</MobileViewNoDataWrapper>
				) : (
					data?.map((record, i) => {
						const dataIndexesInHeader = columns.filter(c => c.showOnMobileRow && c.dataIndex).map(c => c.dataIndex!);
						return (
							<FlexColumn key={`mob_${i}`}>
								<MobileViewHeader record={record} dataIndexesInHeader={dataIndexesInHeader} />
							</FlexColumn>
						);
					})
				)}
			</MobileViewWrapper>
		</Spin>
	);
}
